import { LESSONS_TYPES } from '@yandex-int/k-common/typings';
import { useAppContext, useGeneratePath, useMe, useStaticUrls } from 'platform-components/common';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import ym from 'react-yandex-metrika';
import { Dispatch } from 'redux';

import * as teamActions from 'common.components/classes/actions';
import * as teamSelectors from 'common.components/classes/selectors';
import { getTabToRedirect } from 'common.components/spec-course-lessons/utils';
import { SPEC_COURSE_LESSON_STATUS } from 'common.components/spec-course/typings';
import * as userSelectors from 'common.components/user/selectors';

import { specCourseActions } from '../spec-course.store/actions';
import * as specCourseSelectors from '../spec-course.store/selectors';

import { SpecCourseLessonsPage } from './lessons-page';
import * as selectors from './selectors';

const TYPES_TO_LOAD = [LESSONS_TYPES.ACTIVE, LESSONS_TYPES.ASSIGNED, LESSONS_TYPES.FINISHED];

type Args = {
    classId: number | null;
    specCourseId: number | null;
    dispatch: Dispatch<any>;
};

type InitCoursesArgs = Args & {
    tryRedirectToCourse: (teamId: number, courseId: number | null) => void;
};

export interface Params {
    classId: string;
    courseId: string;
}

const initCourses = (args: InitCoursesArgs) => {
    const { classId, specCourseId, dispatch, tryRedirectToCourse } = args;
    if (classId && !specCourseId) {
        dispatch(specCourseActions.initCourses({ teamId: classId, onSuccess: tryRedirectToCourse }));
    }
};

const loadLessons = (args: Args) => {
    const { classId, specCourseId, dispatch } = args;
    if (classId && specCourseId) {
        dispatch(
            specCourseActions.getLessons({
                courseId: specCourseId,
                types: TYPES_TO_LOAD,
            })
        );
    }
};

type LoadTeamDataArgs = Omit<Args, 'specCourseId'> & {
    shouldLoadTeamData: boolean;
};

const loadTeamData = (args: LoadTeamDataArgs) => {
    const { classId, shouldLoadTeamData, dispatch } = args;
    if (classId && shouldLoadTeamData) {
        dispatch(teamActions.getTeam(classId));
    }
};

const useTryRedirectToCourse = () => {
    const { labClassSpecCourse } = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const history = useHistory();

    return (teamId: number, courseId: number | null) => {
        if (teamId && courseId) {
            history.push(getRouterPath(labClassSpecCourse, { classId: teamId, courseId }));
        }
    };
};

export const SpecCourseLessonsPageContainer = () => {
    const dispatch = useDispatch();
    const me = useMe();

    const { courseId: cId, classId: clId } = useParams<Params>();
    const { initialRender } = useAppContext();
    const classId = Number(clId);
    const specCourseId = Number(cId);

    const shouldLoadLessons = useSelector(specCourseSelectors.getShouldLoadLessons);
    const shouldLoadTeamData = useSelector((store) => teamSelectors.getShouldLoadTeamData(store, { classId }));

    const tryRedirectToCourse = useTryRedirectToCourse();

    useEffect(
        function loadLessonsOnUpdate() {
            if (shouldLoadLessons && !initialRender) {
                dispatch(specCourseActions.clearLessons());
                initCourses({ classId, specCourseId, dispatch, tryRedirectToCourse });
                loadLessons({ classId, specCourseId, dispatch });
                loadTeamData({ classId, shouldLoadTeamData, dispatch });
            }
        },
        // Вызываем загрузку только когда меняются classId, specCourseId
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [classId, specCourseId]
    );

    const handleOpenLesson = (status: SPEC_COURSE_LESSON_STATUS, lessonId: number) => {
        dispatch(specCourseActions.setTab({ tab: getTabToRedirect(status) }));

        ym('reachGoal', 'teacher_oly_take_part', { teacher_id: me?.id, oly_lesson_id: lessonId });
    };

    const clessons = useSelector(specCourseSelectors.getSpecCourseLessons);
    const activeClessons = useSelector(specCourseSelectors.getActiveSpecCourseLessons);
    const completedClessons = useSelector(specCourseSelectors.getCompletedSpecCourseLessons);

    const shouldShowStudentsWarning = useSelector((store) =>
        selectors.getShouldShowStudentsWarning(store, { classId })
    );
    const teamLetter = useSelector((store) => userSelectors.getCurrentTeamLetter(store, { classId }));
    const teamNumber = useSelector((store) => userSelectors.getCurrentTeamLevel(store, { classId }));
    const isLoading = useSelector(specCourseSelectors.getIsSomethingLoading);
    const hasNoLessons = clessons.length === 0 && !isLoading && !initialRender;

    return (
        <SpecCourseLessonsPage
            activeClessons={activeClessons}
            completedClessons={completedClessons}
            hasNoLessons={hasNoLessons}
            isLoading={isLoading}
            onLessonClick={handleOpenLesson}
            shouldShowStudentsWarning={shouldShowStudentsWarning}
            teamLetter={teamLetter}
            teamNumber={teamNumber}
        />
    );
};
